import { gql, useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormControlLabel, FormGroup, Link } from '@mui/material';
import { UbiButton } from 'components/common/elements/UbiButton';
import UbiCheckbox from 'components/common/elements/UbiCheckbox/UbiCheckBox';
import UbiSelect from 'components/common/elements/UbiSelect/UbiSelect';
import UbiTextInput from 'components/common/elements/UbiTextInput/UbiTextInput';
import { SelectItemOption } from 'components/common/sharedInterfaces/input.interfaces';
import { AuthLayout } from 'components/layouts/AuthLayout';
import { Routes } from 'constants/navigation';
import { emailRegex, getUrlPattern } from 'constants/regexes';
import { useSnackbar } from 'context/snackbarContext';
import { useNavigationHelpers } from 'lib/hooks/useNavigationHelpers';
import { useTranslation } from 'lib/hooks/useTranslation';
import { useUrl } from 'lib/hooks/useUrl';
import _ from 'lodash';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import LoadingBar from 'react-top-loading-bar';
import sharedStyle from 'styles/authShared.module.scss';
import * as yup from 'yup';
import style from './Signup.module.scss';

export const signupSchema = yup.object().shape({
    domain: yup.string().matches(getUrlPattern(), 'Enter correct url!').required('Please enter domain'),
    email: yup.string().email('Please enter a valid email').required('Email is required!'),
    name: yup
        .string()
        .matches(/( [A-Za-zÀ-ȕ0-9]([-']?[A-Za-zÀ-ȕ0-9]+)*)/i, 'Please enter Full Name')
        .required('Name is required!'),
    channel: yup.string(),
});

const TOGGLE_SIGNUP = gql`
    mutation SignUp($name: String!, $email: String!, $domain: String!, $channel: String!) {
        signup(payload: { name: $name, email: $email, domain: $domain, channel: $channel }) {
            success
        }
    }
`;

interface SignupFormPayload {
    domain: string;
    name: string;
    email: string;
    channel: string;
}

const initialFormValues: SignupFormPayload = {
    domain: '',
    name: '',
    email: '',
    channel: '',
};

const SignupRight = () => {
    const router = useRouter();
    const { handleSubmit, control, formState, getValues, watch } = useForm({
        defaultValues: initialFormValues,
        resolver: yupResolver(signupSchema),
    });
    const { getLocalizedRoute } = useNavigationHelpers();
    const [toggleMutation] = useMutation(TOGGLE_SIGNUP);
    const [areTermsAccepted, setTermsAccepted] = useState(false);
    const [notAccepted, setNotAccepted] = useState('');
    const { dispatch: snackbarDispatch } = useSnackbar();
    const loadingRef = useRef<any | unknown>(null);
    const watchDomain = watch('domain');
    const [showFullForm, setShowFullForm] = useState(false);

    const onDomainChange = _.debounce(() => {
        if (!showFullForm) {
            const newState = formState.dirtyFields?.domain && getValues('domain') && !formState.errors?.domain;
            if (newState) {
                setShowFullForm(true);
            }
        }
    }, 1500);

    const privacyUrl = useUrl().PrivacyPolicy;
    const termOfServiceUrl = useUrl().TermsOfService;

    useEffect(() => {
        onDomainChange();
    }, [watchDomain]);

    const handleSignup = (variables: SignupFormPayload) => {
        loadingRef?.current?.continuousStart();
        toggleMutation({ variables })
            .then(() => {
                loadingRef?.current?.complete();
                router.push(`${Routes.SignupSuccess}/?email=${getValues('email')}`);
            })
            .catch((e: Error) => {
                loadingRef?.current?.complete();
                snackbarDispatch({
                    type: 'setMessage',
                    payload: e.message,
                });
                snackbarDispatch({
                    type: 'setVisible',
                    payload: true,
                });
            });
    };

    const t = useTranslation();

    const onSubmit = (data: SignupFormPayload) => {
        if (!areTermsAccepted) {
            setNotAccepted('Please accept terms');
            return;
        }
        handleSignup(data);
    };

    const channelOptions: SelectItemOption[] = [
        {
            value: 'The Privacy Whisperer',
            label: 'The Privacy Whisperer',
        },
        {
            value: 'LinkedIn',
            label: 'LinkedIn',
        },
        {
            value: 'Google',
            label: 'Google',
        },
        {
            value: 'Our Privacy Expert Stories',
            label: 'Our Privacy Expert Stories',
        },
        {
            value: 'From a friend or colleague',
            label: 'From a friend or colleague',
        },
        {
            value: 'Others',
            label: 'Others',
        },
    ];

    return (
        <div>
            <LoadingBar color="#4285DE" height={1} ref={loadingRef} shadow={true} />
            <h1 className={sharedStyle.mainHeading}>{t.signup.title}</h1>
            <h2 className={sharedStyle.subHeading}>{t.signup.subtitle}</h2>
            <form className={style.form} onSubmit={handleSubmit(onSubmit)}>
                <div className={style.signupInput}>
                    <UbiTextInput
                        formErrorTObject={t.errors.signUpForm}
                        fieldError={formState.errors?.domain}
                        control={control}
                        rules={{ required: true }}
                        name="domain"
                        label={t.signup.inputs.websiteUrl.label}
                    />
                </div>
                {showFullForm && (
                    <div>
                        <div className={style.signupInput}>
                            <UbiTextInput
                                formErrorTObject={t.errors.signUpForm}
                                fieldError={formState.errors?.email}
                                name="email"
                                control={control}
                                rules={{ required: true, pattern: emailRegex }}
                                type="email"
                                label={t.signup.inputs.workEmail.label}
                            />
                        </div>
                        <div className={style.signupInput}>
                            <UbiTextInput
                                data-hj-suppress
                                formErrorTObject={t.errors.signUpForm}
                                fieldError={formState.errors?.name}
                                control={control}
                                rules={{ required: true }}
                                name="name"
                                label={t.signup.inputs.name.label}
                            />
                        </div>
                    </div>
                )}
                <div className={style.signupInput}>
                    <UbiSelect
                        options={channelOptions}
                        formErrorTObject={t.errors.signUpForm}
                        fieldError={formState.errors?.channel}
                        control={control}
                        rules={{ required: true }}
                        label={t.signup.inputs.channel.label}
                        name="channel"
                    />
                </div>
                <FormGroup style={{ marginTop: '35px' }}>
                    <FormControlLabel //TODO refactor
                        label={
                            <div className={style.textWithLink}>
                                {t.signup.readAndAgree}{' '}
                                <a target="_blank" rel="noreferrer" href={termOfServiceUrl}>
                                    {t.signup.terms}
                                </a>
                                {t.signup.and}
                                <a target="_blank" rel="noreferrer" href={privacyUrl}>
                                    {t.signup.privacyPolicy}
                                </a>
                                {t.signup.end}
                            </div>
                        }
                        control={
                            <UbiCheckbox
                                checked={areTermsAccepted}
                                onChange={() => {
                                    setTermsAccepted(!areTermsAccepted);
                                }}
                            />
                        }
                    />
                    {notAccepted && <p className={style.error}>{notAccepted}</p>}
                </FormGroup>
                <div className={style.signupBtn}>
                    <UbiButton type="submit">{t.signup.btns.signup}</UbiButton>
                </div>
                <div className={sharedStyle.questions}>
                    {t.signup.alreadyHaveAccount} <Link href={getLocalizedRoute(Routes.Login)}>{t.signup.btns.login}</Link>
                </div>
            </form>
        </div>
    );
};

const Signup = () => {
    const t = useTranslation();

    return (
        <>
            <Head>
                <title>{t.seo.signup}</title>
                <meta property="og:title" content={t.seo.signup} />
            </Head>
            <AuthLayout showCardNotice={true} contentRight={<SignupRight />}></AuthLayout>
        </>
    );
};

export default Signup;
